import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for managing recording drafts with auto-save functionality
 * Handles both local storage (fast) and Firebase (backup) storage
 */
export const useDraftManagement = ({
  currentUser,
  audioBlob,
  recordingTime,
  currentVerseIndex,
  selectedBook,
  selectedChapter,
  startVerse,
  endVerse,
  onDraftRestored
}) => {
  // Draft state
  const [hasDraft, setHasDraft] = useState(false);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [hasUnuploadedRecording, setHasUnuploadedRecording] = useState(false);

  // Auto-save recording draft to local storage (fast) - DISABLED during active recording
  const saveRecordingDraftLocal = useCallback(async () => {
    if (!currentUser || !audioBlob) return;
    
    // Don't save drafts during active recording to prevent infinite loops
    console.log('💾 Recording draft auto-save DISABLED during active recording');
    return;
    
    try {
      // Convert audio blob to base64 for local storage
      const base64Audio = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(audioBlob);
      });
      
      const draftData = {
        audioBlob: base64Audio,
        audioType: audioBlob.type,
        recordingTime: recordingTime,
        currentVerseIndex: currentVerseIndex,
        selectedBook: selectedBook,
        selectedChapter: selectedChapter,
        startVerse: startVerse,
        endVerse: endVerse,
        lastSaved: new Date().toISOString(),
        userId: currentUser.uid
      };
      
      // Save to localStorage
      localStorage.setItem(`recording_draft_${currentUser.uid}`, JSON.stringify(draftData));
      console.log('💾 Recording draft auto-saved to local storage');
    } catch (error) {
      console.error('❌ Error saving recording draft to local storage:', error);
    }
  }, [currentUser, audioBlob, recordingTime, currentVerseIndex, selectedBook, selectedChapter, startVerse, endVerse]);

  // Auto-save recording draft to Firestore (backup)
  const saveRecordingDraft = useCallback(async () => {
    if (!currentUser || !audioBlob) return;
    
    try {
      const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      
      // Convert audio blob to base64 for storage
      const base64Audio = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(audioBlob);
      });
      
      const draftData = {
        audioBlob: base64Audio,
        audioType: audioBlob.type,
        recordingTime: recordingTime,
        currentVerseIndex: currentVerseIndex,
        selectedBook: selectedBook,
        selectedChapter: selectedChapter,
        startVerse: startVerse,
        endVerse: endVerse,
        lastSaved: serverTimestamp(),
        userId: currentUser.uid
      };
      
      await setDoc(doc(db, 'recording_drafts', currentUser.uid), draftData);
      console.log('💾 Recording draft auto-saved to Firebase');
    } catch (error) {
      console.error('❌ Error saving recording draft to Firebase:', error);
    }
  }, [currentUser, audioBlob, recordingTime, currentVerseIndex, selectedBook, selectedChapter, startVerse, endVerse]);

  // Restore recording draft from local storage first, then Firebase as backup
  const restoreRecordingDraft = useCallback(async () => {
    if (!currentUser) return null;
    
    try {
      // First, try to restore from local storage (fast)
      const localDraft = localStorage.getItem(`recording_draft_${currentUser.uid}`);
      if (localDraft) {
        const draftData = JSON.parse(localDraft);
        
        // Convert base64 back to blob
        const response = await fetch(draftData.audioBlob);
        const audioBlob = await response.blob();
        
        console.log('💾 Restored recording draft from local storage');
        return {
          audioBlob: audioBlob,
          recordingTime: draftData.recordingTime || 0,
          currentVerseIndex: draftData.currentVerseIndex || 0,
          selectedBook: draftData.selectedBook,
          selectedChapter: draftData.selectedChapter,
          startVerse: draftData.startVerse,
          endVerse: draftData.endVerse
        };
      }
      
      // If no local draft, try Firebase as backup
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      
      const draftDoc = await getDoc(doc(db, 'recording_drafts', currentUser.uid));
      
      if (draftDoc.exists()) {
        const draftData = draftDoc.data();
        
        // Convert base64 back to blob
        const response = await fetch(draftData.audioBlob);
        const audioBlob = await response.blob();
        
        console.log('💾 Restored recording draft from Firebase');
        return {
          audioBlob: audioBlob,
          recordingTime: draftData.recordingTime || 0,
          currentVerseIndex: draftData.currentVerseIndex || 0,
          selectedBook: draftData.selectedBook,
          selectedChapter: draftData.selectedChapter,
          startVerse: draftData.startVerse,
          endVerse: draftData.endVerse
        };
      }
    } catch (error) {
      console.error('❌ Error restoring recording draft:', error);
    }
    
    return null;
  }, [currentUser]);

  // Delete recording draft from both local storage and Firebase
  const deleteRecordingDraft = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      // Delete from local storage first (fast)
      localStorage.removeItem(`recording_draft_${currentUser.uid}`);
      console.log('🗑️ Recording draft deleted from local storage');
      
      // Also delete from Firebase as backup
      const { doc, deleteDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      
      await deleteDoc(doc(db, 'recording_drafts', currentUser.uid));
      console.log('🗑️ Recording draft deleted from Firebase');
      
      setHasDraft(false);
    } catch (error) {
      console.error('❌ Error deleting recording draft:', error);
    }
  }, [currentUser]);

  // Auto-save to local storage whenever audioBlob changes (immediate) - DISABLED during active recording
  useEffect(() => {
    if (audioBlob && currentUser) {
      console.log('💾 Auto-save DISABLED during active recording to prevent infinite loops');
      // saveRecordingDraftLocal(); // DISABLED
    }
  }, [audioBlob, currentUser, saveRecordingDraftLocal]);

  // Auto-save to Firebase every 5 minutes as backup
  useEffect(() => {
    if (audioBlob && currentUser) {
      // Start Firebase backup interval (just as a safety net)
      const firebaseBackupInterval = setInterval(() => {
        saveRecordingDraft(); // Save to Firebase as backup
      }, 300000); // Auto-save every 5 minutes to Firebase
      
      return () => {
        clearInterval(firebaseBackupInterval);
      };
    }
  }, [audioBlob, currentUser, saveRecordingDraft]);

  // Check for existing draft on component mount (only once, not on every render)
  useEffect(() => {
    const checkForDraft = async () => {
      if (!currentUser) return;
      
      // Clear any existing drafts to prevent infinite loops during active recording
      localStorage.removeItem(`recording_draft_${currentUser.uid}`);
      console.log('🔍 DRAFT CHECK: Cleared existing drafts to prevent infinite loops');
      
      // Also clear any existing drafts from the restore function
      const existingDraft = localStorage.getItem(`recording_draft_${currentUser.uid}`);
      if (existingDraft) {
        localStorage.removeItem(`recording_draft_${currentUser.uid}`);
        console.log('🔍 DRAFT CHECK: Removed existing draft from localStorage');
      }
      
      console.log('🔍 DRAFT CHECK: Checking for existing draft...');
      const draft = await restoreRecordingDraft();
      if (draft) {
        console.log('🔍 DRAFT CHECK: Found draft, showing resume dialog:', draft);
        setHasDraft(true);
        setShowResumeDialog(true);
        setHasUnuploadedRecording(true);
        
        // Call the callback to restore the draft data
        if (onDraftRestored) {
          onDraftRestored(draft);
        }
      } else {
        console.log('🔍 DRAFT CHECK: No draft found');
      }
    };
    
    checkForDraft();
  }, [currentUser]); // Removed restoreRecordingDraft and onDraftRestored from dependencies

  // Check for unuploaded recordings (audioBlob exists but not uploaded)
  useEffect(() => {
    if (audioBlob) {
      setHasUnuploadedRecording(true);
    } else {
      setHasUnuploadedRecording(false);
    }
  }, [audioBlob]);

  return {
    // State
    hasDraft,
    showResumeDialog,
    hasUnuploadedRecording,
    
    // Setters
    setHasDraft,
    setShowResumeDialog,
    setHasUnuploadedRecording,
    
    // Functions
    saveRecordingDraftLocal,
    saveRecordingDraft,
    restoreRecordingDraft,
    deleteRecordingDraft
  };
};
